import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Clock, CheckCircle, XCircle, Eye, Upload, Plus, ChevronRight, AlertCircle } from 'lucide-react';
import { applicationsAPI } from '../lib/api';
import { Badge, EmptyState, Modal, LoadingPage } from '../components/shared';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'var(--warning)', label: 'Pending Review' },
  under_review: { icon: Eye, color: 'var(--info)', label: 'Under Review' },
  accepted: { icon: CheckCircle, color: 'var(--success)', label: 'Accepted' },
  rejected: { icon: XCircle, color: 'var(--error)', label: 'Rejected' },
  withdrawn: { icon: AlertCircle, color: 'var(--text-muted)', label: 'Withdrawn' },
};

export default function ApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    applicationsAPI.getMy()
      .then(r => setApplications(r.data))
      .catch(() => toast.error('Failed to load applications'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingPage />;

  const pending = applications.filter(a => a.status === 'pending').length;
  const accepted = applications.filter(a => a.status === 'accepted').length;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">My Applications</h1>
          <p className="page-subtitle">Track your research topic applications</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard/marketplace')}>
            <Plus size={16} /> Browse Topics
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard/projects')}>
            <Upload size={16} /> Independent Project
          </button>
        </div>
      </div>

      {/* Stats Strip */}
      {applications.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 32 }}>
          {[
            { label: 'Total', value: applications.length, color: 'var(--text-primary)' },
            { label: 'Pending', value: pending, color: 'var(--warning)' },
            { label: 'Accepted', value: accepted, color: 'var(--success)' },
            { label: 'Rejected', value: applications.filter(a => a.status === 'rejected').length, color: 'var(--error)' },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding: '16px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.75rem', fontFamily: 'var(--font-display)', fontWeight: 600, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Applications List */}
      {applications.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No applications yet"
          description="Browse the research marketplace and apply to topics that interest you."
          actions={
            <button className="btn btn-primary" onClick={() => navigate('/dashboard/marketplace')}>
              Browse Marketplace
            </button>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {applications.map(app => {
            const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
            const Icon = cfg.icon;
            return (
              <div
                key={app.id}
                className="card"
                style={{ padding: 24, cursor: 'pointer', transition: 'border-color 0.2s' }}
                onClick={() => setSelected(app)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <Icon size={16} style={{ color: cfg.color, flexShrink: 0 }} />
                      <span style={{
                        fontSize: '0.7rem', fontFamily: 'var(--font-mono)',
                        color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.1em'
                      }}>
                        {cfg.label}
                      </span>
                      {app.participation_type && (
                        <span style={{
                          fontSize: '0.65rem', background: 'rgba(255,255,255,0.06)',
                          padding: '2px 8px', borderRadius: 4, color: 'var(--text-muted)'
                        }}>
                          {app.participation_type.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                    <h3 style={{ fontSize: '1.05rem', fontFamily: 'var(--font-display)', fontWeight: 600, marginBottom: 6, color: 'var(--text-primary)' }}>
                      {app.topic_title}
                    </h3>
                    <div style={{ display: 'flex', gap: 16, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {app.lecturer_name && <span>Dr. {app.lecturer_name}</span>}
                      {app.department && <span>· {app.department}</span>}
                      {app.domain && (
                        <span style={{ color: 'var(--teal)' }}>· {app.domain}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 16 }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {new Date(app.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                  </div>
                </div>

                {app.review_notes && (
                  <div style={{
                    marginTop: 14, padding: '10px 14px',
                    background: app.status === 'accepted' ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                    borderRadius: 6, borderLeft: `3px solid ${cfg.color}`,
                    fontSize: '0.8rem', color: 'var(--text-secondary)'
                  }}>
                    <strong>Reviewer note:</strong> {app.review_notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Application Detail Modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Application Details" size="lg">
        {selected && <ApplicationDetail app={selected} onClose={() => setSelected(null)} />}
      </Modal>
    </div>
  );
}

function ApplicationDetail({ app, onClose }) {
  const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;

  return (
    <div className="modal-body">
      {/* Status banner */}
      <div style={{
        padding: '12px 16px', borderRadius: 8, marginBottom: 20,
        background: `${cfg.color}18`, border: `1px solid ${cfg.color}40`,
        display: 'flex', alignItems: 'center', gap: 10
      }}>
        <Icon size={18} style={{ color: cfg.color }} />
        <span style={{ color: cfg.color, fontWeight: 600 }}>{cfg.label}</span>
      </div>

      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', marginBottom: 8 }}>{app.topic_title}</h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        {[
          ['Department', app.department],
          ['Lecturer', app.lecturer_name],
          ['Domain', app.domain],
          ['Type', app.participation_type?.replace(/_/g, ' ')],
          ['Applied', new Date(app.created_at).toLocaleDateString()],
          ['Last Updated', new Date(app.updated_at).toLocaleDateString()],
        ].filter(([, v]) => v).map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Statement of Interest</div>
        <div style={{
          padding: 14, background: 'rgba(255,255,255,0.03)', borderRadius: 8,
          fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6,
          border: 'var(--border-subtle)'
        }}>
          {app.statement_of_interest}
        </div>
      </div>

      {app.proposed_approach && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Proposed Approach</div>
          <div style={{
            padding: 14, background: 'rgba(255,255,255,0.03)', borderRadius: 8,
            fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6,
            border: 'var(--border-subtle)'
          }}>
            {app.proposed_approach}
          </div>
        </div>
      )}

      {app.review_notes && (
        <div style={{
          padding: '12px 16px', borderRadius: 8,
          background: app.status === 'accepted' ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${cfg.color}40`
        }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Reviewer Note</div>
          <div style={{ fontSize: '0.875rem', color: cfg.color }}>{app.review_notes}</div>
        </div>
      )}

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
        {app.is_sponsored && app.status === 'accepted' && (
          <Link to="/dashboard/projects" className="btn btn-primary">View Project →</Link>
        )}
      </div>
    </div>
  );
}
